import { useMemo, type ComponentType } from 'react';
import { Flex, Typography } from 'antd';
import {
  IconAdjustments,
  IconAlertTriangle,
  IconBuilding,
  IconDatabase,
  IconFileText,
  IconForms,
  IconMapPin,
  IconPackage,
  IconReportAnalytics,
  IconShield,
  IconTool,
  IconUsers,
  IconAffiliate,
  IconListCheck,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { PermissionKey } from '@evimo/types/enums/permission-key';
import type { GroupedPermissions, PermissionCategory } from '../../../types';
import { formatPermissionLabel } from '../../../utils';
import { PermissionChip } from '../PermissionChip';

const { Text } = Typography;

const CATEGORY_ICONS: Record<
  PermissionCategory,
  ComponentType<{ size?: number; stroke?: number }>
> = {
  rbacManagement: IconShield,
  users: IconUsers,
  assets: IconPackage,
  sites: IconBuilding,
  zones: IconMapPin,
  incidents: IconAlertTriangle,
  reports: IconReportAnalytics,
  documents: IconFileText,
  customFields: IconForms,
  partners: IconAffiliate,
  maintenance: IconTool,
  diagnosticRules: IconListCheck,
  knowledgeBase: IconDatabase,
  platform: IconAdjustments,
};

type UsePermissionAccordionItemsOptions = {
  groupedPermissions: GroupedPermissions[];
  draftPermissionKeys: PermissionKey[];
  isRoleEditable: boolean;
  isPermissionDirty: (permissionKey: PermissionKey) => boolean;
  suppressDirtyHighlight: boolean;
  onTogglePermission: (permissionKey: PermissionKey) => void;
};

export function usePermissionAccordionItems({
  groupedPermissions,
  draftPermissionKeys,
  isRoleEditable,
  isPermissionDirty,
  suppressDirtyHighlight,
  onTogglePermission,
}: UsePermissionAccordionItemsOptions) {
  const { t } = useTranslation('authorization');

  const draftPermissionKeySet = useMemo(
    () => new Set(draftPermissionKeys),
    [draftPermissionKeys],
  );

  const items = useMemo(
    () =>
      groupedPermissions.map((group) => {
        const CategoryIcon = CATEGORY_ICONS[group.category];
        const grantedCount = group.permissions.filter((permission) =>
          draftPermissionKeySet.has(permission.key),
        ).length;

        return {
          key: group.category,
          label: (
            <Flex
              align="center"
              justify="space-between"
              gap={12}
              className="authorization-permissions-panel-label"
            >
              <Flex
                align="center"
                gap={10}
                className="authorization-permissions-panel-label-main"
              >
                <span
                  className="authorization-permissions-panel-icon"
                  aria-hidden
                >
                  <CategoryIcon size={18} stroke={1.75} />
                </span>
                <Text className="authorization-permissions-panel-title">
                  {t(`categories.${group.category}`)}
                </Text>
              </Flex>
              <Text className="authorization-permissions-panel-count">
                {t('detail.categoryGranted', {
                  granted: grantedCount,
                  total: group.permissions.length,
                })}
              </Text>
            </Flex>
          ),
          children: (
            <div className="authorization-permission-grid">
              {group.permissions.map((permission) => (
                <PermissionChip
                  key={permission.key}
                  permissionKey={permission.key}
                  checked={draftPermissionKeySet.has(permission.key)}
                  showDirtyState={
                    !suppressDirtyHighlight && isPermissionDirty(permission.key)
                  }
                  isRoleEditable={isRoleEditable}
                  onTogglePermission={onTogglePermission}
                  label={formatPermissionLabel(permission.key, t)}
                />
              ))}
            </div>
          ),
        };
      }),
    [
      draftPermissionKeySet,
      groupedPermissions,
      isPermissionDirty,
      isRoleEditable,
      onTogglePermission,
      suppressDirtyHighlight,
      t,
    ],
  );

  return { items };
}
